import * as THREE from 'three';
import { scene, renderer } from './scene.js';
import { colliders, wallMeshes, player, S, STAND } from './state.js';
import { buildGrid, nearBox, nearPoint } from './mapgrid.js';
import { makeMaterialTextures, MAT_TILE } from './maptex.js';
import mapData from '../../server/game/shared/map.json';

// ============================================================
//  Карта: стены/ящики (коллизии + цели рейкаста), неоновые столбы.
//
//  Геометрия грузится из ОБЩЕГО map.json — того же файла, который читает
//  сервер (shared/src/map.rs). Дублировать список боксов в JS нельзя:
//  расхождение геометрии клиента и сервера даёт «толкает в пустоту» и
//  «застрял в стене», а искать причину приходится в netcode, где её нет.
//  Импорт наружу client/ разрешён через server.fs.allow в vite.config.js.
//
//  ⚠️ ГЛАВНОЕ: «во что упираемся» и «что рисуем» — РАЗНЫЕ вещи.
//  `colliders` — все 1743 бокса из map.json, как есть, слово в слово с сервером.
//  Рисуется же только ВИДИМАЯ ОБОЛОЧКА: грани, упирающиеся в соседний бокс,
//  выбрасываются ещё при загрузке.
//
//  ⚠️ БАГ-УРОК «после смены карты FPS упал с 60 до 20». Импортированная
//  de_dust2 вокселизована СПЛОШНЯКОМ: здания и скалы залиты боксами изнутри,
//  73% объёма арены — тело геометрии. Замер показал 9.6 бокса на среднем луче
//  взгляда (в худшем — 290): каждый пиксель экрана затенялся ~10 раз подряд
//  материалом MeshStandardMaterial с мягкими тенями, и почти всё это была
//  внутренность камня, которую не видно никогда. Диагноз подтверждается за
//  10 секунд: сжать окно браузера — если FPS вырос кратно площади, упор во
//  fill rate, а не в CPU. Лечится тем, что здесь и сделано:
//    1. 53% граней скрыты соседями — их не существует для GPU вовсе;
//    2. остаток нарезан на чанки по XZ, а не свален в один меш на карту:
//       чанк вне поля зрения three.js отбрасывает целиком, а видимые сортирует
//       спереди-назад, и тест глубины отсекает закрытые пиксели ДО затенения.
//  Раньше здесь был один InstancedMesh на материал: он экономил вызовы
//  отрисовки (их было 6), но и куллинг, и сортировку выключал начисто —
//  инстансы у three.js не сортируются и не отбрасываются поштучно.
//  На блокауте из 57 коробок это не стоило ничего, на 1743 — стоило втрое.
//
//  ⚠️ Следствие для рейкастов: `wallMeshes` теперь чанки, а не боксы. Луч
//  по-прежнему упирается в оболочку (это и есть поверхность карты), и стало
//  БЫСТРЕЕ — у чанка есть сфера охвата, мимо которой луч отбрасывается сразу.
//  Единственный изменившийся случай: луч, ВЫПУЩЕННЫЙ ИЗ КАМНЯ, теперь не
//  найдёт стену на выходе (внутренних граней нет). Игрок внутрь попасть не
//  может, а гранате там делать нечего.
// ============================================================

// Цвет и фактура берутся из МАТЕРИАЛА поверхности. Материал не у бокса, а в
// отдельном слое `map.json::paint` — там записано, где песок, где дерево, где
// бетон; классы конвертер извлёк из имён текстур исходного BSP
// (см. md/map-import.md). Цвет кладётся в вершины, фактуру даёт процедурная
// текстура (maptex.js), одинаковая по геометрии на всю карту.
//
// ⚠️ Почему материал не полем у бокса: он резал бы боксы по границам
// материалов (2068 → 7764), а по коллайдерам сервер бегает линейно каждый
// тик. Полное объяснение — в комментарии у слоя `paint` в конвертере.
/** Материал-метка «в это упираются, но это не рисуют» — небо из BSP.
 *  ⚠️ Зеркало строки, которую пишет `tools/bsp-to-boxes.mjs`. */
const SKY = 'sky';

const FALLBACK = '#9aa7b8';
const _col = new THREE.Color();
function colorFor(kind) {
  _col.set((mapData.palette && mapData.palette[kind || 'wall']) || FALLBACK);
  return [_col.r, _col.g, _col.b];
}

// ---- 1. Коллайдеры: ровно то, что в map.json ----
for (const b of mapData.boxes) {
  colliders.push({
    min: new THREE.Vector3(b.p[0] - b.s[0] / 2, b.p[1] - b.s[1] / 2, b.p[2] - b.s[2] / 2),
    max: new THREE.Vector3(b.p[0] + b.s[0] / 2, b.p[1] + b.s[1] / 2, b.p[2] + b.s[2] / 2),
    w: b.s[0], d: b.s[2],
    m: b.m || 'wall',
  });
}
buildGrid();

// ---- 2. Отбрасывание граней, закрытых соседним боксом ----

const EPS = 1e-4;

/**
 * Покрыт ли прямоугольник грани соседями целиком.
 *
 * Считаем не «пересекается ли», а именно «закрыт ли весь»: режем грань
 * координатами краёв соседей и проверяем каждую получившуюся клетку. Дырка
 * в один сантиметр — это дырка в стене, сквозь которую видно чёрное нутро,
 * поэтому проверка точная, а не выборочная.
 */
function fullyCovered(u0, u1, v0, v1, rects) {
  if (!rects.length) return false;
  const us = [u0, u1], vs = [v0, v1];
  for (const r of rects) {
    if (r[0] > u0 + EPS && r[0] < u1 - EPS) us.push(r[0]);
    if (r[1] > u0 + EPS && r[1] < u1 - EPS) us.push(r[1]);
    if (r[2] > v0 + EPS && r[2] < v1 - EPS) vs.push(r[2]);
    if (r[3] > v0 + EPS && r[3] < v1 - EPS) vs.push(r[3]);
  }
  us.sort((a, b) => a - b); vs.sort((a, b) => a - b);
  for (let i = 0; i < us.length - 1; i++) {
    if (us[i + 1] - us[i] < EPS) continue;
    const um = (us[i] + us[i + 1]) / 2;
    for (let j = 0; j < vs.length - 1; j++) {
      if (vs[j + 1] - vs[j] < EPS) continue;
      const vm = (vs[j] + vs[j + 1]) / 2;
      let ok = false;
      for (const r of rects) {
        if (um > r[0] && um < r[1] && vm > r[2] && vm < r[3]) { ok = true; break; }
      }
      if (!ok) return false;
    }
  }
  return true;
}

const AX = ['x', 'y', 'z'];
const _rects = [];

// ---- 2.5. Воксельные сетки: «где твердь» и «где какой материал» ----
//
// Обе живут на ТОЙ ЖЕ сетке, на которой конвертер вокселизовал карту (её шаг
// и начало приезжают в `paint`), поэтому границы боксов ложатся точно на
// линии сетки и ни один запрос не попадает «между ячеек».
//
// ⚠️ Зачем своя сетка, если есть `mapgrid.js`. Та отвечает на вопрос «какие
// боксы рядом» и пригодна для физики; здесь же нужен ДРУГОЙ вопрос — «твердь
// ли вот в этой точке» — и задаётся он сотни тысяч раз при запекании AO.
// Массив байт отвечает за одно обращение, перебор боксов — за десятки.
// ⚠️ И ещё: `nearBox` не реентерабельна (общий буфер), а запекание идёт
// внутри обхода граней, который сам её вызывает.
const PAINT = mapData.paint || null;
/** Имена материалов; номер в этом списке = номер слоя в массиве текстур. */
const MAT_NAMES = PAINT ? PAINT.mats : ['wall'];
const GO = PAINT ? PAINT.origin : [0, 0, 0];     // начало сетки, м
const GC = PAINT ? PAINT.cell : [0.5, 0.4, 0.5]; // шаг сетки по осям, м

let GN = [1, 1, 1];                              // размеры сетки в ячейках
let solidGrid = null;                            // 1 — твердь (небо НЕ считается)
let matGrid = null;                              // 0 — нет краски, иначе слой+1

{
  const t = performance.now();
  let mx = -Infinity, my = -Infinity, mz = -Infinity;
  for (const c of colliders) {
    if (c.max.x > mx) mx = c.max.x;
    if (c.max.y > my) my = c.max.y;
    if (c.max.z > mz) mz = c.max.z;
  }
  GN = [
    Math.max(1, Math.ceil((mx - GO[0]) / GC[0]) + 1),
    Math.max(1, Math.ceil((my - GO[1]) / GC[1]) + 1),
    Math.max(1, Math.ceil((mz - GO[2]) / GC[2]) + 1),
  ];
  const total = GN[0] * GN[1] * GN[2];
  solidGrid = new Uint8Array(total);
  matGrid = new Uint8Array(total);

  const gi = (i, j, k) => (j * GN[2] + k) * GN[0] + i;
  const lo = (v, o, c) => Math.max(0, Math.round((v - o) / c));
  const hi = (v, o, c, n) => Math.min(n, Math.round((v - o) / c));

  // ⚠️ Небо в твердь НЕ идёт: оно невидимо, и если позволить ему затенять,
  // вся карта получит фальшивую тень от несуществующей крыши.
  for (const c of colliders) {
    if (c.m === SKY) continue;
    const i0 = lo(c.min.x, GO[0], GC[0]), i1 = hi(c.max.x, GO[0], GC[0], GN[0]);
    const j0 = lo(c.min.y, GO[1], GC[1]), j1 = hi(c.max.y, GO[1], GC[1], GN[1]);
    const k0 = lo(c.min.z, GO[2], GC[2]), k1 = hi(c.max.z, GO[2], GC[2], GN[2]);
    for (let j = j0; j < j1; j++) {
      for (let k = k0; k < k1; k++) {
        const row = (j * GN[2] + k) * GN[0];
        for (let i = i0; i < i1; i++) solidGrid[row + i] = 1;
      }
    }
  }

  // Раскраска: записи «ix,iy,iz,ширина,высота,глубина,материал» через ';'.
  let paintCells = 0;
  if (PAINT && PAINT.v) {
    for (const rec of PAINT.v.split(';')) {
      if (!rec) continue;
      const a = rec.split(',');
      const i0 = +a[0], j0 = +a[1], k0 = +a[2];
      const w = +a[3], h = +a[4], d = +a[5], m = +a[6] + 1;
      for (let j = j0; j < j0 + h && j < GN[1]; j++) {
        for (let k = k0; k < k0 + d && k < GN[2]; k++) {
          for (let i = i0; i < i0 + w && i < GN[0]; i++) { matGrid[gi(i, j, k)] = m; paintCells++; }
        }
      }
    }
  }
  console.log(`[карта] сетка ${GN.join('×')} = ${(total / 1e6).toFixed(1)} млн ячеек, `
    + `раскрашено ${(paintCells / 1000).toFixed(0)}к (${(performance.now() - t).toFixed(0)} мс)`);
}

/** Твердь ли в точке (мировые метры). За пределами сетки — нет. */
function solidAt(x, y, z) {
  const i = Math.floor((x - GO[0]) / GC[0]);
  if (i < 0 || i >= GN[0]) return 0;
  const j = Math.floor((y - GO[1]) / GC[1]);
  if (j < 0 || j >= GN[1]) return 0;
  const k = Math.floor((z - GO[2]) / GC[2]);
  if (k < 0 || k >= GN[2]) return 0;
  return solidGrid[(j * GN[2] + k) * GN[0] + i];
}

/** Слой материала в точке, или −1 если эта ячейка не окрашена. */
function matAt(x, y, z) {
  const i = Math.floor((x - GO[0]) / GC[0]);
  if (i < 0 || i >= GN[0]) return -1;
  const j = Math.floor((y - GO[1]) / GC[1]);
  if (j < 0 || j >= GN[1]) return -1;
  const k = Math.floor((z - GO[2]) / GC[2]);
  if (k < 0 || k >= GN[2]) return -1;
  return matGrid[(j * GN[2] + k) * GN[0] + i] - 1;
}

/** Видна ли грань бокса `c` по оси `a` со стороны `s` (0 — min, 1 — max). */
function faceVisible(c, a, s) {
  const ka = AX[a], ku = AX[(a + 1) % 3], kv = AX[(a + 2) % 3];
  const plane = s ? c.max[ka] : c.min[ka];

  // Грань, смотрящая вниз из-под уровня пола, не видна никогда: пол — сплошная
  // непрозрачная плоскость (scene.js), под неё попасть нельзя. Это выбрасывает
  // в том числе исподнюю сторону плиты земли — квад 124×90 м во весь экран.
  if (a === 1 && s === 0 && plane <= 0.02) return false;

  _rects.length = 0;
  nearBox(c.min.x - 0.05, c.min.z - 0.05, c.max.x + 0.05, c.max.z + 0.05, (o) => {
    if (o === c) return;
    // ⚠️ Небо соседом НЕ считается. Оно твёрдое, но невидимое, и если позволить
    // ему «закрывать» грань камня, то на всём стыке карты с небом откроются
    // дыры в чёрное нутро: грань не нарисуют, а закрыть её нечем.
    if (o.m === SKY) return;
    // сосед должен занимать пространство ВПЛОТНУЮ ЗА гранью, а не перед ней
    const probe = s ? plane + EPS : plane - EPS;
    if (o.min[ka] > probe || o.max[ka] < probe) return;
    if (o.max[ku] <= c.min[ku] + EPS || o.min[ku] >= c.max[ku] - EPS) return;
    if (o.max[kv] <= c.min[kv] + EPS || o.min[kv] >= c.max[kv] - EPS) return;
    _rects.push([o.min[ku], o.max[ku], o.min[kv], o.max[kv]]);
  });
  return !fullyCovered(c.min[ku], c.max[ku], c.min[kv], c.max[kv], _rects);
}

// ---- 3. Сборка видимых граней в чанки ----

/**
 * Сторона чанка, м. 28 → 30 мешей на карте 140 м.
 *
 * ⚠️ БАГ-УРОК: 14 м пробовали и ОТКАТИЛИ. Рассуждение было стройное — чанк
 * это единица сортировки спереди-назад, мельче чанк → точнее сортировка →
 * меньше закрытых пикселей шейдится; замер отбора по полю зрения его
 * подтверждал (в кадре 35% чанков вместо 48%). На деле у владельца стало
 * ХУЖЕ: масштаб просел с 85% до 80%, добавились провалы до 40 кадров.
 *
 * Причина, скорее всего, в цене вызова отрисовки: 85 чанков вместо 30, да ещё
 * вдвое из-за прохода теней — это ~170 вызовов на кадр, и на связке Mesa +
 * встроенная Intel накладные расходы драйвера съели всю экономию по пикселям.
 * Проверять и подтверждать это отдельно не стали: важнее было вернуть игроку
 * рабочую частоту кадров, чем доказать красивую гипотезу.
 *
 * ⚠️ Вывод общий: «в кадре меньше процентов чанков» — не то же самое, что
 * «кадр стал дешевле». Замер посредника (доля чанков) подтвердил гипотезу,
 * а замер того, что нужно на самом деле (кадры у игрока), её опроверг.
 */
const CHUNK = 28;

// ---- Запекание AO и материала в вершины ----
//
// ⚠️ ЧТО ЭТО ДАЁТ И ПОЧЕМУ ИМЕННО ТАК. Жалоба владельца была «карта скучная,
// цвета сливаются, не видно персонажей». Половину этого лечит не текстура,
// а ТЕНЬ В УГЛАХ: без неё стена, пол и ящик стыкуются встык, кадр читается
// как аппликация, и силуэт игрока не за что зацепить глазу. Настоящий
// запечённый свет (lightmap) требует развёртки всей карты в атлас — это
// отдельная большая работа (см. md/graphics.md, «карты всерьёз»).
// Здесь же затенение считается ПО СОСЕДЯМ и кладётся в цвет вершины:
// в кадре оно не стоит ни одной инструкции — цвет вершин и так уже был.
//
// Цена — вершины: грань приходится дробить, иначе затенение растянется
// на всю стену. Дробим шагом в ДВЕ ячейки сетки и тут же склеиваем обратно
// (`mergeFlat`) всё, что вышло ровным, — поэтому чистая стена остаётся одним
// квадом, а лесенка теней появляется только вокруг стыков.

/**
 * Насколько тёмным становится глухой угол (доля от базового цвета).
 *
 * ⚠️ 0.45, а было 0.62. Само затенение считается верно, но оно НЕ единственное,
 * что темнит угол: там же на тёмный материал ложится тень от солнца, и
 * множители перемножаются. При 0.62 угол получал 0.38 от базы, а теневая грань
 * — ещё вшестеро меньше солнечной: тёмное дерево в углу уходило почти в чёрный
 * («невзрачно», жалоба владельца). Правилось парой со заливкой в scene.js:
 * поднять только свет — и затенение снова растворилось бы, убрать только
 * затенение — карта опять стала бы аппликацией без объёма.
 */
const AO_STRENGTH = 0.45;

/**
 * Затенение в точке грани: насколько занят твердью ВОЗДУШНЫЙ слой вокруг неё.
 *
 * Считаем по площадке 4×4 ячейки (≈2×2 м) с центром в точке: ближние ячейки
 * весом 1, дальние — 0.5.
 *
 * ⚠️ БАГ-УРОК «AO есть в числах, но его не видно». Сначала здесь стояли ровно
 * четыре ячейки вплотную (±полшага). Логика безупречная, счётчик показывал
 * «73% узлов затенены» — а на скриншоте стыки стены с полом оставались
 * стерильно ровными, зато нутро карты под зданиями было чёрным.
 * Причина в РАЗНЫХ МАСШТАБАХ: узлы стоят через 1 м (шаг дробления), а такое
 * ядро видит твердь только в 0.25 м от себя. Стена, попавшая между двумя
 * узлами, не затемняла НИ ОДИН из них — сигнал был вдвое мельче, чем частота,
 * которой его снимают. Затемнялось только то, что закрыто целиком.
 * ⚠️ Правило на будущее: ширина ядра затенения обязана быть НЕ МЕНЬШЕ шага
 * дробления грани — меняешь `su`/`sv`, проверь и это.
 */
const AO_TAPS = [-1.5, -0.5, 0.5, 1.5];
function aoAt(a, s, plane, au, u, av, v) {
  const n = s ? 1 : -1;
  const p = [0, 0, 0];
  p[a] = plane + n * GC[a] * 0.5;
  let occ = 0, wsum = 0;
  for (const du of AO_TAPS) {
    const wu = Math.abs(du) < 1 ? 1 : 0.5;
    p[au] = u + du * GC[au];
    for (const dv of AO_TAPS) {
      const w = wu * (Math.abs(dv) < 1 ? 1 : 0.5);
      p[av] = v + dv * GC[av];
      occ += w * solidAt(p[0], p[1], p[2]);
      wsum += w;
    }
  }
  return 1 - AO_STRENGTH * (occ / wsum);
}

/** Разброс тона по боксу: одинаковая стена перестаёт быть одной заливкой. */
function jitterOf(c) {
  // детерминированный хеш от координат — при перезагрузке карта та же
  let h = Math.imul(Math.round(c.min.x * 4) * 73856093 ^ Math.round(c.min.y * 4) * 19349663
    ^ Math.round(c.min.z * 4) * 83492791, 0x45d9f3b);
  h = (h ^ (h >>> 15)) >>> 0;
  return 0.94 + (h % 1000) / 1000 * 0.12;
}

const MAT_FALLBACK = Math.max(0, MAT_NAMES.indexOf('wall'));

/**
 * Одна видимая грань бокса: дробление, материал на каждый кусок, AO по узлам,
 * обратная склейка ровных кусков.
 */
function pushFace(buf, c, a, s) {
  const au = (a + 1) % 3, av = (a + 2) % 3;
  const ka = AX[a], ku = AX[au], kv = AX[av];
  const plane = s ? c.max[ka] : c.min[ka];
  const u0 = c.min[ku], u1 = c.max[ku], v0 = c.min[kv], v1 = c.max[kv];
  const jit = jitterOf(c);

  // Шаг дробления — две ячейки сетки по каждой оси грани. Так узлы всегда
  // лежат на линиях сетки (условие корректного AO), а частота вдвое ниже,
  // чем у геометрии: тень в углу шириной ~1 м выглядит как мягкий контакт,
  // а не как чёрная кайма в один воксель.
  const su = GC[au] * 2, sv = GC[av] * 2;
  const nu = Math.max(1, Math.round((u1 - u0) / su));
  const nv = Math.max(1, Math.round((v1 - v0) / sv));
  const uAt = (i) => (i >= nu ? u1 : u0 + i * su);
  const vAt = (j) => (j >= nv ? v1 : v0 + j * sv);

  // --- клетки: что погребено соседом ---
  //
  // ⚠️ ЭТО НЕ ОПТИМИЗАЦИЯ «НА ВСЯКИЙ СЛУЧАЙ», А ОБЯЗАТЕЛЬНЫЙ ШАГ. `faceVisible`
  // выбрасывает грань только целиком: плита пола 124×90 м не закрыта целиком
  // никогда, поэтому рисуется вся — вместе с той частью, что лежит ПОД
  // зданиями. Пока грань была одним квадом, это стоило четыре вершины. С
  // дроблением на метровые куски оно стало стоить 1.06 МЛН вершин на карту
  // (замер), из которых видно хорошо если четверть.
  //
  // Здесь та же проверка, что в `faceVisible`, но на клетку: кусок грани
  // выбрасывается, если ВСЕ ячейки перед ним заняты твердью. Дырок это дать
  // не может — вся геометрия карты выровнена по этой же сетке, а частично
  // закрытый кусок остаётся на месте.
  const hidden = new Uint8Array(nu * nv);
  {
    const n = s ? 1 : -1;
    const p = [0, 0, 0];
    p[a] = plane + n * GC[a] * 0.5;
    const cell = (val, ax) => Math.round((val - GO[ax]) / GC[ax]);
    for (let j = 0; j < nv; j++) {
      const k0 = cell(vAt(j), av), k1 = cell(vAt(j + 1), av);
      for (let i = 0; i < nu; i++) {
        const i0 = cell(uAt(i), au), i1 = cell(uAt(i + 1), au);
        let all = 1;
        for (let kk = k0; kk < k1 && all; kk++) {
          p[av] = GO[av] + (kk + 0.5) * GC[av];
          for (let ii = i0; ii < i1; ii++) {
            p[au] = GO[au] + (ii + 0.5) * GC[au];
            if (!solidAt(p[0], p[1], p[2])) { all = 0; break; }
          }
        }
        hidden[j * nu + i] = all;
      }
    }
  }

  // --- узлы: затенение ---
  //
  // ⚠️ Считается ПОСЛЕ отбрасывания погребённых кусков и только у тех узлов, к
  // которым примыкает хоть один живой кусок: запекание — самая дорогая часть
  // сборки карты (16 выборок сетки на узел), и половина узлов гигантской плиты
  // пола иначе считалась бы ради кусков, которые тут же выбрасываются.
  const ao = new Float32Array((nu + 1) * (nv + 1)).fill(1);
  for (let j = 0; j <= nv; j++) {
    for (let i = 0; i <= nu; i++) {
      let live = false;
      for (let dj = -1; dj <= 0 && !live; dj++) {
        for (let di = -1; di <= 0; di++) {
          const ci = i + di, cj = j + dj;
          if (ci >= 0 && cj >= 0 && ci < nu && cj < nv && !hidden[cj * nu + ci]) { live = true; break; }
        }
      }
      if (live) ao[j * (nu + 1) + i] = aoAt(a, s, plane, au, uAt(i), av, vAt(j));
    }
  }

  // --- клетки: материал ---
  // Смотрим в ячейку ПОД гранью (внутрь тверди): именно её красил конвертер.
  const inset = GC[a] * 0.4 * (s ? -1 : 1);
  const mat = new Int16Array(nu * nv);
  let anyMat = false;
  {
    const p = [0, 0, 0];
    for (let j = 0; j < nv; j++) {
      for (let i = 0; i < nu; i++) {
        p[a] = plane + inset;
        p[au] = (uAt(i) + uAt(i + 1)) / 2;
        p[av] = (vAt(j) + vAt(j + 1)) / 2;
        const m = matAt(p[0], p[1], p[2]);
        mat[j * nu + i] = m;
        if (m >= 0) anyMat = true;
      }
    }
    // Куски грани, закрытые другой геометрией, конвертер не красил (их не
    // видно). Но грань рисуется целиком, поэтому пустоты заполняем ближайшим
    // найденным материалом — иначе сквозь щели проглядывали бы куски чужого
    // цвета. Заливка вширь по клеткам грани.
    if (anyMat) {
      const q = [];
      for (let k = 0; k < mat.length; k++) if (mat[k] >= 0) q.push(k);
      for (let h = 0; h < q.length; h++) {
        const k = q[h], i = k % nu, j = (k - i) / nu;
        for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const ni = i + di, nj = j + dj;
          if (ni < 0 || nj < 0 || ni >= nu || nj >= nv) continue;
          const nk = nj * nu + ni;
          if (mat[nk] >= 0) continue;
          mat[nk] = mat[k]; q.push(nk);
        }
      }
    } else {
      mat.fill(MAT_FALLBACK);
    }
  }

  // --- склейка ровных кусков ---
  //
  // Клетку можно слить с соседями, только если затенение ровное И У НЕЁ,
  // И У ВСЕХ СОСЕДЕЙ. ⚠️ Второе условие не перестраховка: слитый
  // прямоугольник интерполирует цвет между своими углами, и если рядом
  // окажется клетка с тенью, по общему ребру пойдёт разрыв освещения
  // (классический T-стык). Полоса неслитых клеток вокруг тени его снимает.
  const dirty = new Uint8Array(nu * nv);
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const flat = ao[j * (nu + 1) + i] > 0.999 && ao[j * (nu + 1) + i + 1] > 0.999
        && ao[(j + 1) * (nu + 1) + i] > 0.999 && ao[(j + 1) * (nu + 1) + i + 1] > 0.999;
      if (!flat) dirty[j * nu + i] = 1;
    }
  }
  const blocked = new Uint8Array(nu * nv);
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      let bad = 0;
      for (let dj = -1; dj <= 1 && !bad; dj++) {
        for (let di = -1; di <= 1; di++) {
          const ni = i + di, nj = j + dj;
          if (ni < 0 || nj < 0 || ni >= nu || nj >= nv) continue;
          if (dirty[nj * nu + ni]) { bad = 1; break; }
        }
      }
      blocked[j * nu + i] = bad;
    }
  }

  const used = hidden;   // погребённые куски считаем уже «занятыми» — их не рисуем
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const k = j * nu + i;
      if (used[k]) continue;
      const m = mat[k];
      let w = 1, h = 1;
      if (!blocked[k]) {
        const ok = (x, y) => !used[y * nu + x] && !blocked[y * nu + x] && mat[y * nu + x] === m;
        while (i + w < nu && ok(i + w, j)) w++;
        grow: while (j + h < nv) {
          for (let x = 0; x < w; x++) if (!ok(i + x, j + h)) break grow;
          h++;
        }
      }
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) used[(j + y) * nu + i + x] = 1;
      emitQuad(buf, a, s, plane, au, av, uAt(i), uAt(i + w), vAt(j), vAt(j + h),
        ao, nu, i, j, i + w, j + h, m, jit);
    }
  }
}

/** Один квад грани: позиции, нормаль, цвет с AO, UV в тайлах, номер слоя. */
function emitQuad(buf, a, s, plane, au, av, ua, ub, va, vb, ao, nu, i0, j0, i1, j1, m, jit) {
  const rgb = colorFor(MAT_NAMES[m] || 'wall');
  const tile = MAT_TILE[MAT_NAMES[m]] || 2;
  const nrm = [0, 0, 0];
  nrm[a] = s ? 1 : -1;

  // ⚠️ Ось V текстуры смотрит ВВЕРХ на боковых гранях. От этого зависят ряды
  // кладки, полосы карниза и доски «стоймя» (см. соглашение в maptex.js):
  // перепутаешь — кладка ляжет вертикальными столбиками. У граней «вверх/вниз»
  // верха нет, там годится любой порядок.
  const flipUV = a === 0;

  const vert = (u, v, ai, aj) => {
    const p = [0, 0, 0];
    p[a] = plane; p[au] = u; p[av] = v;
    buf.pos.push(p[0], p[1], p[2]);
    buf.nrm.push(nrm[0], nrm[1], nrm[2]);
    const k = ao[aj * (nu + 1) + ai] * jit;
    buf.col.push(rgb[0] * k, rgb[1] * k, rgb[2] * k);
    buf.uv.push((flipUV ? v : u) / tile, (flipUV ? u : v) / tile);
    buf.lay.push(m);
  };
  // ⚠️ Четыре вершины и ИНДЕКСЫ, а не шесть вершин. Раньше грань была одним
  // квадом на бокс, и разница была незаметна; после дробления на метровые
  // куски лишняя треть вершин — это лишняя треть работы вершинного шейдера
  // в каждом из трёх проходов (глубина, тени, цвет).
  const base = buf.pos.length / 3;
  vert(ua, va, i0, j0); vert(ub, va, i1, j0); vert(ub, vb, i1, j1); vert(ua, vb, i0, j1);
  // Порядок обхода зависит от стороны — иначе половина граней окажется
  // вывернутой наизнанку и пропадёт при отсечении задних граней.
  if (s) buf.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  else buf.idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
}

/**
 * Горизонтальная заплатка пола на `y = 0` (см. блок 3.5 ниже).
 *
 * Отдельно от `emitQuad`, потому что тому нужен массив AO по узлам грани, а у
 * заплатки узлов нет: она не грань бокса, а ровный кусок земли. Затенение здесь
 * и не нужно — затенять её нечем, соседней геометрии у дыры по определению нет.
 *
 * ⚠️ Раскладка вершин и порядок обхода — зеркало `emitQuad` для случая
 * «грань вверх» (`a = 1, s = 1`): ось U это Z, ось V это X. Перепутаешь —
 * заплатка окажется вывернутой наизнанку и пропадёт при отсечении задних граней.
 */
function emitPatch(buf, x0, x1, z0, z1, m) {
  const rgb = colorFor(MAT_NAMES[m] || 'wall');
  const tile = MAT_TILE[MAT_NAMES[m]] || 2;
  const vert = (z, x) => {
    buf.pos.push(x, 0, z);
    buf.nrm.push(0, 1, 0);
    buf.col.push(rgb[0], rgb[1], rgb[2]);
    buf.uv.push(z / tile, x / tile);
    buf.lay.push(m);
  };
  const base = buf.pos.length / 3;
  vert(z0, x0); vert(z1, x0); vert(z1, x1); vert(z0, x1);
  buf.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

const chunks = new Map();
let facesTotal = 0, facesKept = 0, skyBoxes = 0;
const tBuild = performance.now();
for (const c of colliders) {
  // ⚠️ НЕБО НЕ РИСУЕТСЯ. В GoldSrc небо — твёрдые браши: они ограничивают
  // карту и останавливают пули, но движок вместо них показывает скайбокс.
  // Мы упираемся в них так же (они герметизируют карту и держат ботов внутри),
  // но в кадр не отдаём — иначе над картой висит крыша, которой в оригинале
  // нет. Разбор — баг-урок в md/bug-lessons.md.
  if (c.m === SKY) { skyBoxes++; continue; }
  const key = `${Math.floor((c.min.x + c.max.x) / 2 / CHUNK)}:${Math.floor((c.min.z + c.max.z) / 2 / CHUNK)}`;
  let buf = chunks.get(key);
  if (!buf) { buf = { pos: [], nrm: [], col: [], uv: [], lay: [], idx: [] }; chunks.set(key, buf); }
  for (let a = 0; a < 3; a++) {
    for (let s = 0; s < 2; s++) {
      facesTotal++;
      if (!faceVisible(c, a, s)) continue;
      facesKept++;
      pushFace(buf, c, a, s);
    }
  }
}

// ---- 3.5. Заплатки пола: там, где карта дырявая ----
//
// ⚠️ ЗАЧЕМ. В `map.json` есть места, где пола нет вовсе, а игрок туда заходит
// и там стоит: найдено по жалобе владельца 2026-07-28, точка `x 45.72 z 21.40`,
// вся дыра — `x 42.5…49.5, z 18.5…28` (~62 м²). Разбор — в md/status.md.
// Видно там было серо-синюю сетку страховочной плоскости из scene.js, то есть
// «пол из стандартных квадратов вместо ландшафта», а при движении она ещё и
// переливалась муаром (тайл 2 м, анизотропия 4, сглаживание выключено) —
// с чего жалоба и началась как «пол меняет цвет».
//
// ⚠️ ПОЧЕМУ НА `y = 0`, А НЕ ПОЧИНКОЙ КАРТЫ. Физика при отсутствии опоры и так
// возвращает ноль (`movement.rs::support_height`), то есть игрок в дыре стоит
// ровно на нулевой высоте. Значит нарисованный на нуле пол СОВПАДАЕТ с тем, на
// чём человек стоит, и никакого расхождения клиента с сервером не вносит —
// в отличие от подъёма страховочной плоскости, которая лежит на −0.45 и дала
// бы ноги в воздухе на 45 см (а поднять её нельзя: получим z-fighting с плитой
// земли, баг-урок «около пола просвечивает солнце»).
//
// ⚠️ Это ЗАПЛАТКА, а не починка. Настоящее место ошибки — конвертер
// `tools/bsp-to-boxes.mjs`, который эту твердь не выдал; там дыра и остаётся,
// поэтому дырой она числится в md/status.md до сих пор. Здесь закрыт только
// симптом — вид. Пуля, граната и звук по-прежнему считают дыру пустотой.
{
  const t = performance.now();
  // Габариты настоящей карты: за них не заливаем. Небо в счёт не идёт — оно
  // торчит далеко за геометрию, и по нему заплатки уехали бы в чистое поле.
  let bx0 = Infinity, bx1 = -Infinity, bz0 = Infinity, bz1 = -Infinity;
  for (const c of colliders) {
    if (c.m === SKY) continue;
    if (c.min.x < bx0) bx0 = c.min.x;
    if (c.max.x > bx1) bx1 = c.max.x;
    if (c.min.z < bz0) bz0 = c.min.z;
    if (c.max.z > bz1) bz1 = c.max.z;
  }

  const ni = GN[0], nk = GN[2];
  const cx = (i) => GO[0] + (i + 0.5) * GC[0];
  const cz = (k) => GO[2] + (k + 0.5) * GC[2];
  // Слой ячеек ПОД нулём (y −0.4…0) и слой НАД ним — оба нужны: дыра это
  // «под ногами пусто», но если сразу над нулём твердь, то мы внутри монолита,
  // и пол там не нужен никому.
  const below = -GC[1] * 0.5, above = GC[1] * 0.5;

  const hole = new Uint8Array(ni * nk);
  let holeCells = 0;
  for (let k = 0; k < nk; k++) {
    const z = cz(k);
    if (z < bz0 || z > bz1) continue;
    for (let i = 0; i < ni; i++) {
      const x = cx(i);
      if (x < bx0 || x > bx1) continue;
      if (solidAt(x, below, z)) continue;   // опора есть — не дыра
      if (solidAt(x, above, z)) continue;   // мы внутри тверди — пол не нужен
      hole[k * ni + i] = 1; holeCells++;
    }
  }

  if (holeCells) {
    // Материал заплатки — от ближайшего окрашенного пола вокруг. Иначе пятно
    // возьмёт запасной `wall` и будет читаться заплаткой не хуже сетки.
    const matOf = (i, k) => {
      for (let r = 1; r <= 24; r++) {
        for (let d = -r; d <= r; d++) {
          for (const [pi, pk] of [[i + d, k - r], [i + d, k + r], [i - r, k + d], [i + r, k + d]]) {
            if (pi < 0 || pk < 0 || pi >= ni || pk >= nk) continue;
            if (hole[pk * ni + pi]) continue;
            const m = matAt(cx(pi), below, cz(pk));
            if (m >= 0) return m;
          }
        }
      }
      return MAT_FALLBACK;
    };

    // Слияние в прямоугольники — тем же приёмом, что и склейка граней выше:
    // поодиночке 0.5-метровые клетки дали бы вчетверо больше вершин впустую.
    const used = new Uint8Array(ni * nk);
    let patches = 0;
    for (let k = 0; k < nk; k++) {
      for (let i = 0; i < ni; i++) {
        const at = k * ni + i;
        if (!hole[at] || used[at]) continue;
        const m = matOf(i, k);
        const ok = (x, y) => hole[y * ni + x] && !used[y * ni + x] && matOf(x, y) === m;
        let w = 1, h = 1;
        while (i + w < ni && ok(i + w, k)) w++;
        grow: while (k + h < nk) {
          for (let x = 0; x < w; x++) if (!ok(i + x, k + h)) break grow;
          h++;
        }
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) used[(k + y) * ni + i + x] = 1;

        const x0 = GO[0] + i * GC[0], x1 = GO[0] + (i + w) * GC[0];
        const z0 = GO[2] + k * GC[2], z1 = GO[2] + (k + h) * GC[2];
        const key = `${Math.floor((x0 + x1) / 2 / CHUNK)}:${Math.floor((z0 + z1) / 2 / CHUNK)}`;
        let buf = chunks.get(key);
        if (!buf) { buf = { pos: [], nrm: [], col: [], uv: [], lay: [], idx: [] }; chunks.set(key, buf); }
        emitPatch(buf, x0, x1, z0, z1, m);
        patches++;
      }
    }
    console.log(`[карта] заплатки пола: ${holeCells} ячеек без опоры → ${patches} квадов `
      + `(${(performance.now() - t).toFixed(0)} мс)`);
  }
}

// Материал один на всю карту: цвет приходит вершинами.
//
// ⚠️ ЛАМБЕРТ, А НЕ STANDARD — и это не экономия на спичках, а самый крупный
// рычаг из всех, что здесь применены. `MeshStandardMaterial` — это физически
// корректный PBR: на КАЖДЫЙ фрагмент он считает GGX-спекуляр, Френеля и
// энергосохраняющий диффуз. Мы за всё это платим и ничего не получаем: карта
// без текстур, шероховатость стояла 0.9, то есть блик и так был подавлен
// почти в ноль. `MeshLambertMaterial` даёт тот же матовый диффуз в разы
// дешевле, а разницу на некрашеных боксах увидеть нельзя.
//
// ⚠️ Правило на будущее: PBR оправдан там, где есть карты материалов
// (шероховатость, металл, нормали). Текстура появилась, но она ОДНОКАНАЛЬНАЯ
// (только яркость, см. maptex.js) — ни нормалей, ни металличности, ни
// шероховатости у неё нет, блику по-прежнему неоткуда взяться. Ламберт
// остаётся верным выбором; Standard возвращать только вместе с настоящими
// картами материалов и с новым замером.
const mapMat = new THREE.MeshLambertMaterial({ vertexColors: true });

// ---- Фактура материалов: одна выборка текстуры на пиксель карты ----
//
// ⚠️ ТРЕБУЕТ WebGL2 (`sampler2DArray`). На WebGL1 просто остаётся раскраска
// вершинами с запечённым AO — карта выглядит беднее, но играется и не падает.
// Проверять надо ИМЕННО возможности рендерера, а не «есть ли где-то WebGL2»:
// контекст мог свалиться в программный рендеринг (см. `[графика]` в scene.js).
const texOK = renderer.capabilities.isWebGL2;
if (texOK) {
  const matTex = makeMaterialTextures(MAT_NAMES);
  matTex.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  mapMat.onBeforeCompile = (sh) => {
    sh.uniforms.matTex = { value: matTex };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        attribute vec2 tileUv;   // UV в тайлах материала, посчитаны на CPU
        attribute float matLayer;
        varying vec2 vTileUv;
        varying float vMatLayer;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vTileUv = tileUv; vMatLayer = matLayer;`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform sampler2DArray matTex;
        varying vec2 vTileUv;
        varying float vMatLayer;`)
      // после <color_fragment>: там в diffuseColor уже лёг цвет вершины
      // (материал + AO + разброс тона). Текстура — множитель поверх него.
      // ⚠️ 1.4167 = 255/180 — зеркало константы MID в maptex.js: байт 180
      // означает «ничего не менять».
      .replace('#include <color_fragment>', `#include <color_fragment>
        diffuseColor.rgb *= texture(matTex, vec3(vTileUv, vMatLayer)).r * 1.4167;`);
  };
  // Без своего ключа three кэширует программу по набору штатных признаков —
  // а наши правки в него не входят.
  mapMat.customProgramCacheKey = () => 'map-mat-array';
}

// ⚠️ ПРЕДЗАПОЛНЕНИЕ ГЛУБИНЫ (depth prepass). Карта рисуется ДВАЖДЫ: сперва
// пустышкой, которая пишет только глубину, и лишь потом по-настоящему.
// Пустышка считает только позицию вершин, зато оставляет в буфере глубины
// точное «ближе этого ничего нет» — и настоящий проход отсекается тестом
// глубины ДО вызова фрагментного шейдера.
//
// Почему работает без правки композера: у three.js непрозрачные объекты
// сортируются сперва по `renderOrder`, поэтому −1 гарантированно уводит
// пустышку вперёд. Тест глубины по умолчанию LEQUAL, геометрия та же самая
// до бита — настоящий проход проходит впритык, без z-fighting.
//
// ⚠️ Пустышке ОБЯЗАТЕЛЬНО castShadow = false: иначе она удвоит проход теней.
//
// ⚠️ БАГ-УРОК: это ПРОБОВАЛИ УБРАТЬ и вернули. Рассуждение было такое: приём
// окупается только при дорогом фрагментном шейдере, а после перехода на Ламберт
// и PCF дорогого шейдера не стало (что подтверждали два замера — выключение
// теней и блума не меняли кадры ВООБЩЕ), значит платим ~8 лишних растеризаций
// на пиксель зря. Логика выглядела железной. У владельца стало хуже: 80%
// вместо 85% и провалы до 40 кадров. Вернули.
//
// ⚠️ Мораль, ради которой это здесь записано: на реальном железе стоимость
// прохода геометрии и стоимость шейдинга не складываются так, как подсказывает
// здравый смысл, и «по идее должно быть быстрее» — не аргумент. Прежде чем
// снимать этот код в следующий раз, СНАЧАЛА замерьте у игрока, потом снимайте.
const depthOnly = new THREE.MeshBasicMaterial({ colorWrite: false });

for (const [, buf] of chunks) {
  if (!buf.pos.length) continue;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(buf.pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(buf.nrm, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(buf.col, 3));
  if (texOK) {
    g.setAttribute('tileUv', new THREE.Float32BufferAttribute(buf.uv, 2));
    // ⚠️ Слой — Uint8 без нормализации: материалов восемь, во float его гнать
    // незачем. В шейдере он приходит как float — это делает сам WebGL.
    g.setAttribute('matLayer', new THREE.Uint8BufferAttribute(buf.lay, 1));
  }
  g.setIndex(buf.idx);
  g.computeBoundingSphere();

  const pre = new THREE.Mesh(g, depthOnly);   // геометрия ОБЩАЯ с настоящим мешем
  pre.renderOrder = -1;
  pre.castShadow = pre.receiveShadow = false;
  scene.add(pre);

  const mesh = new THREE.Mesh(g, mapMat);
  mesh.castShadow = mesh.receiveShadow = true;
  scene.add(mesh);
  // ⚠️ В wallMeshes кладём ТОЛЬКО настоящий меш: пустышка — та же геометрия,
  // и рейкаст находил бы каждую стену дважды.
  wallMeshes.push(mesh);
}

// Видно в консоли сразу после загрузки — это те самые числа, ради которых
// всё выше и написано. Если после смены карты доля скрытых граней рухнет,
// значит конвертер стал выдавать что-то другое, и FPS упадёт следом.
let verts = 0;
for (const [, buf] of chunks) verts += buf.pos.length / 3;
console.log(`[карта] боксов ${colliders.length} (небо ${skyBoxes} — не рисуется), `
  + `граней ${facesKept}/${facesTotal} (скрыто ${Math.round((1 - facesKept / facesTotal) * 100)}%), `
  + `чанков ${wallMeshes.length}, вершин ${(verts / 1000).toFixed(0)}к, `
  + `сборка ${(performance.now() - tBuild).toFixed(0)} мс`
  + (texOK ? '' : ' — БЕЗ ТЕКСТУР (нет WebGL2)'));

// ---- 4. Неоновые столбы — ориентиры зон, а не украшение углов ----
//
// ⚠️ Координаты берутся из `labels` в map.json, а не зашиты числами. Раньше
// они были константами и после смены карты оказались внутри зданий: столб,
// замурованный в стену, не ориентир, а мусор. Теперь точки едут вместе с
// картой — их пишет тот же конвертер, который её собирает.
const NEON = {
  'ПЛЕЙТ A': 0xff2d78,
  // синий даёт мало яркости (люминанс) → без повышенной интенсивности почти не блумит
  'ПЛЕЙТ B': 0x3b82f6,
  'МИД': 0x00ffcc,
  'ЛОНГ': 0xffd000,
  'T СПАВН': 0x8f6bb0,
  'CT СПАВН': 0x5fa86a,
};

/// Высота опоры под точкой — чтобы столб стоял на полу, а не торчал из него.
/// Карта рельефная: фиксированная высота закопала бы столб на любой террасе.
function supportAt(x, z) {
  let y = 0;
  nearPoint(x, z, 0.01, (c) => {
    if (x > c.min.x && x < c.max.x && z > c.min.z && z < c.max.z && c.max.y > y) y = c.max.y;
  });
  return y;
}

/// Точка появления игрока для КЛИЕНТСКИХ нужд: до первого снапшота (пока идёт
/// подключение) и в офлайне, где сервера нет вовсе.
///
/// ⚠️ В онлайне авторитет — сервер (`movement::Phys::spawn_on`), и его позиция
/// приезжает ближайшим `OP_SELF`. Здесь нужен лишь вменяемый старт: жёсткая
/// точка `(0, STAND, 22)` осталась от рукописной арены, а на de_dust2 это
/// середина карты и высота «пол на нуле» — игрок стартовал внутри геометрии
/// и видел карту снизу (баг-урок «появляюсь под картой», см. md/bug-lessons.md).
///
/// Высота — грубая: верх ближайшего бокса под точкой, без разбора уровней
/// «под мостом / на мосту», который делает сервер. Для первого спавна карты
/// этого достаточно (над ним ничего нет), а неточность живёт максимум до
/// первого снапшота.
export function spawnPoint() {
  const s = (mapData.spawns || [])[0];
  if (!s) return { x: 0, y: STAND, z: 22, yaw: Math.PI };
  return { x: s.p[0], y: supportAt(s.p[0], s.p[1]) + STAND, z: s.p[1], yaw: s.yaw ?? Math.PI };
}

// Стартовая позиция игрока — сразу на спавне карты, а не в точке из state.js:
// там `player` объявлен до того, как карта вообще загружена, и поставить его
// на пол можно только здесь, после наполнения `colliders`.
{
  const sp = spawnPoint();
  player.pos.set(sp.x, sp.y, sp.z);
  S.yaw = sp.yaw;
}

for (const l of mapData.labels || []) {
  const color = NEON[l.t];
  if (color === undefined) continue;
  const intensity = color === 0x3b82f6 ? 6.0 : 3.0;
  // эмиссия с интенсивностью >1 → в HDR-буфере ярче 1 → попадает под порог bloom
  const m = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 4.4, 10),
    new THREE.MeshStandardMaterial({ color: 0x111111, emissive: color, emissiveIntensity: intensity }));
  m.position.set(l.p[0], supportAt(l.p[0], l.p[1]) + 2.2, l.p[1]);
  scene.add(m);
}
